import React, { useState, useEffect } from "react";
import Modal from "../../../components/modal";
import { Country, State, City } from "country-state-city";

const CompanyCustomerForm = ({ show, onClose, heading, initialData = {}, onSubmit }) => {
    // State to manage form inputs
    const [formData, setFormData] = useState({
        name: '',
        company_name: '',
        phone: '',
        email: '',
        gst_no: '',
        address: '',
        country: 'India',
        state: 'West Bengal',
        city: 'Kolkata',
        dl_number: '',
        aadhar_no: '',
        is_wholesaler: 0,
        tan_number: '',
        pan_number: '',
        ismsme: 0,
        msme_number: '',
        ref_name: '',
        ref_number: '',
    });

    // Load initial data into form if editing an existing customer
    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                company_name: initialData.company_name || '',
                phone: initialData.phone || initialData.mobile || '',
                email: initialData.email || '',
                gst_no: initialData.gst_no || initialData.gst_number || '',
                address: initialData.address || '',
                country: initialData.country || 'India',
                state: initialData.state || 'West Bengal',
                city: initialData.city || 'Kolkata',
                dl_number: initialData.dl_number || initialData.dl_no || '',
                aadhar_no: initialData.aadhar_no || initialData.aadhar_number || '',
                is_wholesaler: initialData.is_wholesaler ? 1 : 0,
                tan_number: initialData.tan_number || '',
                pan_number: initialData.pan_number || '',
                ismsme: initialData.ismsme || initialData.is_msme || 0,
                msme_number: initialData.msme_number || '',
                ref_name: initialData.ref_name || '',
                ref_number: initialData.ref_number || '',
            });
        }
    }, [initialData]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked ? 1 : 0 });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    // Country, State, City options
    const countries = Country.getAllCountries();
    const selectedCountryObj = countries.find(c => c.name === formData.country);
    const states = selectedCountryObj ? State.getStatesOfCountry(selectedCountryObj.isoCode) : [];
    const selectedStateObj = states.find(s => s.name === formData.state);
    const cities = (selectedCountryObj && selectedStateObj) ? City.getCitiesOfState(selectedCountryObj.isoCode, selectedStateObj.isoCode) : [];

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Modal
            show={show}
            onClose={onClose}
            heading={heading}
            size="lg"
        >
            <form onSubmit={handleSubmit}>
                <div className="row">
                    
                    {/* Wholesaler Checkbox Toggle Banner */}
                    <div className="col-12 mb-3">
                        <div className="p-3 bg-light-primary border border-primary rounded d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="fw-bold mb-0 text-primary">Wholesaler Customer</h6>
                                <small className="text-muted">Enable if this customer is a Wholesaler / Dealer</small>
                            </div>
                            <div className="form-check form-switch fs-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="is_wholesaler"
                                    id="is_wholesaler_toggle"
                                    checked={!!formData.is_wholesaler}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Contact Person Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="name"
                                className="form-control"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter Contact Person Name"
                                required
                            />
                        </div>
                    </div>
                    
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Company Name</label>
                            <input
                                type="text"
                                name="company_name"
                                className="form-control"
                                value={formData.company_name}
                                onChange={handleChange}
                                placeholder="Enter Company Name"
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Contact Person Phone <span className="text-danger">*</span></label>
                            <input
                                type="tel"
                                name="phone"
                                className="form-control"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Enter Phone Number"
                                required
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Contact Person Email</label>
                            <input
                                type="email"
                                name="email"
                                className="form-control"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter Email Address"
                            />
                        </div>
                    </div>

                    {/* Country, State, City */}
                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Country</label>
                            <select
                                name="country"
                                className="form-control"
                                value={formData.country}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        country: e.target.value,
                                        state: '',
                                        city: ''
                                    });
                                }}
                            >
                                <option value="">-- Select Country --</option>
                                {countries.map(c => (
                                    <option key={c.isoCode} value={c.name}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">State</label>
                            <select
                                name="state"
                                className="form-control"
                                value={formData.state}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        state: e.target.value,
                                        city: ''
                                    });
                                }}
                            >
                                <option value="">-- Select State --</option>
                                {states.map(s => (
                                    <option key={s.isoCode} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">City</label>
                            <select
                                name="city"
                                className="form-control"
                                value={formData.city}
                                onChange={handleChange}
                            >
                                <option value="">-- Select City --</option>
                                {cities.map(ct => (
                                    <option key={ct.name} value={ct.name}>{ct.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* DL No. & Aadhar No. */}
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Drug License (DL) No.</label>
                            <input
                                type="text"
                                name="dl_number"
                                className="form-control"
                                value={formData.dl_number}
                                onChange={handleChange}
                                placeholder="Enter Drug License (DL) Number"
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Aadhar No. <span className="text-muted fw-normal">(Optional)</span></label>
                            <input
                                type="text"
                                name="aadhar_no"
                                className="form-control"
                                value={formData.aadhar_no}
                                onChange={handleChange}
                                placeholder="Enter 12-digit Aadhar Number"
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">GST Number (Optional)</label>
                            <input
                                type="text"
                                name="gst_no"
                                className="form-control"
                                value={formData.gst_no}
                                onChange={handleChange}
                                placeholder="Enter GST Number"
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">PAN Number</label>
                            <input
                                type="text"
                                name="pan_number"
                                className="form-control"
                                value={formData.pan_number}
                                onChange={handleChange}
                                placeholder="Enter PAN Number"
                            />
                        </div>
                    </div>

                    <div className="col-md-12 mb-3">
                        <div className="form-group">
                            <label className="fw-semibold">Full Address</label>
                            <input
                                type="text"
                                name="address"
                                className="form-control"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Enter Full Address"
                                required
                            />
                        </div>
                    </div>

                    <div className="col-md-12 mb-3">
                        <div className="row">
                            <div className="form-check col-md-4 ms-2">
                                <input
                                    type="checkbox"
                                    name="ismsme"
                                    className="form-check-input"
                                    id="ismsme_check"
                                    onChange={handleChange}
                                    checked={!!formData.ismsme}
                                />
                                <label className="form-check-label fw-semibold" htmlFor="ismsme_check">IS MSME Registered</label>
                            </div>
                            {!!formData.ismsme && (
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="msme_number"
                                            className="form-control"
                                            value={formData.msme_number}
                                            onChange={handleChange}
                                            placeholder="Enter MSME Number"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">TAN Number (Optional)</label>
                            <input
                                type="text"
                                name="tan_number"
                                className="form-control"
                                value={formData.tan_number}
                                onChange={handleChange}
                                placeholder="Enter TAN Number"
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-semibold">Reference Name</label>
                            <input
                                type="text"
                                name="ref_name"
                                className="form-control"
                                value={formData.ref_name}
                                onChange={handleChange}
                                placeholder="Enter Reference Name"
                            />
                        </div>
                    </div>

                </div>

                <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary px-4">
                        Save Customer
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default CompanyCustomerForm;
